import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CheckCircle2, Info, Plus, RotateCcw, Search, Users } from '../ui/icons';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Colors, { withAlpha } from '../../constants/Colors';
import { appSettingsApi, InternalAccount } from '../../services/api';

// Stored in {env}-settings under setting-key "internal-access-accounts" (see
// HomeStream-Admin-Backend routers/app_settings.py). The analytics backend matches these
// case-insensitively against the GA user id / 'user' / 'email' params and RTDB sessions.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_ACCOUNTS = 500;

// Paste-friendly: one entry per line, or comma/semicolon/space separated.
function splitAccounts(raw: string): string[] {
  return raw
    .split(/[\s,;]+/)
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
}

export default function InternalAccountsSettings() {
  const [accounts, setAccounts] = useState<InternalAccount[]>([]);
  const [savedAccounts, setSavedAccounts] = useState<InternalAccount[]>([]);
  const [draft, setDraft] = useState('');
  const [draftNote, setDraftNote] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savedMeta, setSavedMeta] = useState<{ exists: boolean; updatedAt?: string | null; table?: string }>({ exists: false });

  const dirty = JSON.stringify(accounts) !== JSON.stringify(savedAccounts);

  const loadSetting = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const response = await appSettingsApi.getInternalAccountsSetting();
    if (response.success && response.data) {
      setAccounts(response.data.setting.accounts);
      setSavedAccounts(response.data.setting.accounts);
      setSavedMeta({ exists: response.data.exists, updatedAt: response.data.updated_at, table: response.data.table_name });
    } else {
      setLoadError(response.error || 'Failed to load internal accounts');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSetting();
  }, [loadSetting]);

  const handleAdd = () => {
    const entries = splitAccounts(draft);
    if (entries.length === 0) return;
    const existing = new Set(accounts.map((a) => a.account));
    const fresh = Array.from(new Set(entries)).filter((entry) => !existing.has(entry));
    const skipped = entries.length - fresh.length;
    if (accounts.length + fresh.length > MAX_ACCOUNTS) {
      toast.error(`Up to ${MAX_ACCOUNTS} internal accounts.`);
      return;
    }
    const note = draftNote.trim() || null;
    setAccounts((current) => [...fresh.map((account) => ({ account, note })), ...current]);
    setDraft('');
    setDraftNote('');
    if (skipped > 0) toast(`${skipped} already in the list`, { icon: 'ℹ️' });
  };

  const handleDelete = (account: string) => {
    setAccounts((current) => current.filter((a) => a.account !== account));
  };

  const handleSave = async () => {
    setSaving(true);
    const response = await appSettingsApi.saveInternalAccountsSetting({ accounts });
    setSaving(false);
    if (response.success && response.data) {
      setAccounts(response.data.setting.accounts);
      setSavedAccounts(response.data.setting.accounts);
      setSavedMeta({ exists: true, updatedAt: response.data.updated_at, table: response.data.table_name });
      toast.success('Internal accounts saved');
    } else {
      toast.error(response.error || 'Failed to save internal accounts');
    }
  };

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return accounts;
    return accounts.filter((a) => a.account.includes(needle) || (a.note || '').toLowerCase().includes(needle));
  }, [accounts, query]);

  const draftEntries = splitAccounts(draft);
  const draftHasNonEmail = draftEntries.some((entry) => !EMAIL_RE.test(entry));

  return (
    <Card
      title="Internal Accounts"
      description="Team and test accounts left out of User Audio Analytics (live listeners, dropped, free trial and historic logs)."
    >
      {loadError && (
        <div className="alert alert-danger" style={{ marginBottom: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
          <span>{loadError}</span>
          <Button variant="outline" className="btn-small" onClick={loadSetting}>Retry</Button>
        </div>
      )}

      {/* Summary */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          padding: '14px 16px',
          borderRadius: '14px',
          marginBottom: '18px',
          background: `linear-gradient(135deg, ${withAlpha(Colors.primary, 0.1)}, ${withAlpha(Colors.secondary, 0.04)})`,
          border: `1px solid ${withAlpha(Colors.primary, 0.18)}`,
        }}
      >
        <span
          style={{
            flex: 'none',
            width: '44px',
            height: '44px',
            borderRadius: '12px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: Colors.primary,
            color: '#FFFFFF',
          }}
        >
          <Users size={20} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '22px', fontWeight: 800, color: Colors.primary, lineHeight: 1.1 }}>
            {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'}
          </div>
          <div style={{ fontSize: '12.5px', color: Colors.textLight, marginTop: '3px' }}>
            Matched by email or app user id, case-insensitive. Looking up one user directly still shows their data.
          </div>
        </div>
      </div>

      <fieldset disabled={loading || !!loadError} style={{ border: 0, padding: 0, margin: 0, opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
        {/* Add */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '420px', marginBottom: '18px' }}>
          <input
            className="form-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAdd();
              }
            }}
            placeholder="Email or user id (paste several, comma or space separated)"
            aria-label="Internal account"
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              className="form-input"
              value={draftNote}
              onChange={(e) => setDraftNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              placeholder="Note (optional), e.g. QA iPhone"
              maxLength={120}
              aria-label="Note"
              style={{ flex: 1, minWidth: 0 }}
            />
            <Button variant="primary" className="btn-small" onClick={handleAdd} disabled={draftEntries.length === 0}>
              <Plus size={14} />
              Add
            </Button>
          </div>
          {draftHasNonEmail && (
            <span style={{ fontSize: '11.5px', color: Colors.textLight }}>
              Not an email - it will be matched as an app user id.
            </span>
          )}
        </div>

        {/* List */}
        {accounts.length > 8 && (
          <div style={{ position: 'relative', maxWidth: '420px', marginBottom: '10px' }}>
            <Search size={14} color={Colors.textLight} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              className="form-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search accounts..."
              aria-label="Search accounts"
              style={{ paddingLeft: '30px' }}
            />
          </div>
        )}

        {!loading && accounts.length === 0 ? (
          <div style={{ fontSize: '12.5px', color: Colors.textLight, padding: '10px 0' }}>
            No internal accounts yet - every user is counted in analytics.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxWidth: '560px' }}>
            {visible.map((entry) => (
              <div
                key={entry.account}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 10px 8px 12px',
                  borderRadius: '10px',
                  border: `1px solid ${withAlpha(Colors.border, 0.14)}`,
                  background: '#FFFFFF',
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 700, color: Colors.text, fontFamily: "'SF Mono', monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.account}
                  </div>
                  {entry.note && <div style={{ fontSize: '11.5px', color: Colors.textLight, marginTop: '1px' }}>{entry.note}</div>}
                </div>
                {!savedAccounts.some((a) => a.account === entry.account) && (
                  <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '999px', background: withAlpha(Colors.secondary, 0.14), color: Colors.secondary }}>
                    new
                  </span>
                )}
                <Button variant="outline" className="btn-small" onClick={() => handleDelete(entry.account)}>
                  Delete
                </Button>
              </div>
            ))}
            {visible.length === 0 && (
              <div style={{ fontSize: '12.5px', color: Colors.textLight, padding: '6px 0' }}>No accounts match "{query}".</div>
            )}
          </div>
        )}
      </fieldset>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          marginTop: '20px',
          paddingTop: '14px',
          borderTop: `1px solid ${withAlpha(Colors.border, 0.14)}`,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: dirty ? '#B45309' : Colors.textLight }}>
          {dirty ? <Info size={14} /> : <CheckCircle2 size={14} />}
          {loading
            ? 'Loading saved accounts...'
            : dirty
              ? 'Unsaved changes'
              : savedMeta.exists
                ? `Saved${savedMeta.updatedAt ? ` ${new Date(savedMeta.updatedAt).toLocaleString()}` : ''}${savedMeta.table ? ` · ${savedMeta.table}` : ''}`
                : 'Not saved yet'}
        </span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button variant="outline" className="btn-small" onClick={() => setAccounts(savedAccounts)} disabled={!dirty || saving}>
            <RotateCcw size={14} />
            Reset
          </Button>
          <Button variant="primary" className="btn-small" onClick={handleSave} loading={saving} disabled={loading || !!loadError || !dirty}>
            Save
          </Button>
        </div>
      </div>
    </Card>
  );
}
